import { autoinject, bindable } from 'aurelia-framework';
import { AsyncErrorHandler } from 'lib/ui';
import { PluckingListDto, categoryDimensionTypes, MooringCertificateListItem, UnitOfMeasure } from 'models/mooring';
import { ErrorService, MooringCertificateService } from 'services';
import { SearchResult } from 'elements/search/search-input';
import { I18N } from 'aurelia-i18n';
import { clone } from 'lib/helpers';

@autoinject
export class PluckList {
  @bindable
  public pluckList: PluckingListDto[] = [];

  @bindable
  protected onSave: (args: { entries: PluckingListDto[] }) => void;

  constructor(private t: I18N, private certificateService: MooringCertificateService, private errorService: ErrorService) {}

  private getDimensionString(item: MooringCertificateListItem) {
    const primaryDimensionType = categoryDimensionTypes.find((x) => x.value == item.PrimaryDimensionType);
    const secondaryDimensionType = categoryDimensionTypes.find((x) => x.value == item.SecondaryDimensionType);

    let dimension = `${item.PrimaryDimension} ${this.t.tr(primaryDimensionType?.labelShort)}`;
    if (secondaryDimensionType) {
      dimension += ` / ${item.SecondaryDimension} ${this.t.tr(secondaryDimensionType?.labelShort)}`;
    }

    return dimension;
  }

  protected onCertificateSelected(item: SearchResult<MooringCertificateListItem>, index: number) {
    const line = this.pluckList[index];

    if (!item || !item.return) {
      throw new Error('Invalid certificate item selected');
    }

    if (!line) {
      throw new Error(`No line found for index ${index}`);
    }

    if (!line.Certificates) {
      line.Certificates = [];
    }

    line.Certificates.push({
      BatchNumber: item.return.BatchNumber,
      BatchNumberTo: item.return.BatchNumberTo,
      ActualBatchNo: '',
      CertificateId: item.return.Id,
      Quantity: 0, // Default quantity, can be adjusted later
      UnitOfMeasure: UnitOfMeasure.meter,
    });
  }

  protected removeRow(lineIndex: number, certificateIndex: number) {
    const line = this.pluckList[lineIndex];

    if (!line || !line.Certificates) {
      throw new Error(`No line or certificates found for index ${lineIndex}`);
    }

    if (certificateIndex > -1) {
      line.Certificates.splice(certificateIndex, 1);
    }
  }

  protected handleSave() {
    this.onSave({ entries: clone(this.pluckList) });
  }

  @AsyncErrorHandler
  protected async searchForCertificate(articleNumber: string, term: string): Promise<SearchResult[]> {
    try {
      const result = await this.certificateService.seach(term, 0, articleNumber);

      if (!result || !result.length || result === undefined) return [];

      return result.map((x) => ({
        id: x.Id,
        title: `${this.t.tr('general.article')}: ${x.ArticleNo}`,
        meta: `${this.t.tr('general.batchNumber')}: ${x.BatchNumber} ${x.BatchNumberTo ? ' - ' + x.BatchNumberTo : ''}`,
        extra: [
          `${x.CategoryName} - ${x.TypeName} - ${x.ProductTypeName} (${this.getDimensionString(x)})`,
          `${this.t.tr('general.producer')}: ${x.SupplierName?.slice(0, 30)}`,
        ],
        return: x,
      }));
    } catch (error) {
      this.errorService.handleError(error);
    }
    return [];
  }
}
